import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add,
  ArrowBack,
  CloudDownload,
  CloudUpload,
  Delete,
  Description,
  Edit,
  Refresh,
  Visibility,
  Warning
} from '@mui/icons-material';
import { ROUTES } from '../../../routes';
import { STAT_FIELDS, DOCUMENT_TYPE_MAPPING } from '../../../Configurations/StatFieldData';
import { API_ENDPOINTS, API_BASE_V1 } from '../../../Api/endpoints';
import { addAuthHeader, getAuthToken } from '../../../utils/authUtils';
import axios from 'axios';

// Utility: robustly normalize to backend snake_case using STAT_FIELDS and DOCUMENT_TYPE_MAPPING
const normalizeBackendDocType = (input) => {
  if (!input) return input;
  const raw = String(input).trim();
  // If already snake_case, standardize to lowercase
  if (raw.includes('_')) return raw.toLowerCase();
  // Direct mapping from frontend camelCase key
  if (DOCUMENT_TYPE_MAPPING[raw]) return DOCUMENT_TYPE_MAPPING[raw];
  // If it's a label, find the key in STAT_FIELDS then map
  const labelMatch = Object.entries(STAT_FIELDS).find(([, v]) => String(v.label || '').toLowerCase() === raw.toLowerCase());
  if (labelMatch) {
    const key = labelMatch[0];
    if (DOCUMENT_TYPE_MAPPING[key]) return DOCUMENT_TYPE_MAPPING[key];
  }
  // Loose match: remove spaces/underscores and compare against known keys/labels
  const loose = raw.toLowerCase().replace(/[_\s]/g, '');
  // Try STAT_FIELDS keys
  const statKeyLoose = Object.keys(STAT_FIELDS).find(k => k.toLowerCase().replace(/[_\s]/g, '') === loose);
  if (statKeyLoose && DOCUMENT_TYPE_MAPPING[statKeyLoose]) return DOCUMENT_TYPE_MAPPING[statKeyLoose];
  // Try STAT_FIELDS labels
  const byLabelLoose = Object.entries(STAT_FIELDS).find(([, v]) => String(v.label || '').toLowerCase().replace(/[_\s]/g, '') === loose);
  if (byLabelLoose && DOCUMENT_TYPE_MAPPING[byLabelLoose[0]]) return DOCUMENT_TYPE_MAPPING[byLabelLoose[0]];
  // Try mapping values (already backend types) loosely
  const docValLoose = Object.values(DOCUMENT_TYPE_MAPPING).find(v => String(v).toLowerCase().replace(/[_\s]/g, '') === loose);
  if (docValLoose) return docValLoose;
  // Fallback: camelCase to snake_case lower
  return raw.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
};

// Document categories are now fetched from the API (/v1/document-types)
// instead of being derived from StatFieldData. See documentCategories inside the component.

// Removed mock data; using real API responses only per user rules

// Format date for display, with optional timestamp
const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (includeTime) {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Calculate days until expiry
const calculateDaysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  
  const expiry = new Date(expiryDate);
  const today = new Date();
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

function DocumentManagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadData, setUploadData] = useState({
    files: [],
    category: '',
    expiryDate: '',
    description: ''
  });
  const [uploadErrors, setUploadErrors] = useState({});
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null); // holds { files:[], backendDocType, uploads:[{fileId,filePath,fileName,fileSize}] }
  // Replace flow (per-row simple replace action)
  const [replaceTarget, setReplaceTarget] = useState(null); // the document row selected for replace
  const replaceFileInputRef = useRef(null);

  // Employee document types fetched from API (replaces STAT_FIELDS-derived DOCUMENT_CATEGORIES)
  const [employeeDocTypes, setEmployeeDocTypes] = useState([]);
  useEffect(() => {
    const fetchEmployeeDocTypes = async () => {
      try {
        const token = getAuthToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_V1}/document-types`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const list = await res.json();
        if (Array.isArray(list)) setEmployeeDocTypes(list);
      } catch (e) { /* ignore */ }
    };
    fetchEmployeeDocTypes();
  }, []);
  const documentCategories = useMemo(() => {
    const cats = employeeDocTypes.map(dt => ({ value: dt.code, label: dt.label }));
    cats.push({ value: 'other', label: 'Other' });
    return cats;
  }, [employeeDocTypes]);

  // Fetch employee data from API
  const fetchEmployeeData = async (employeeId) => {
    try {
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES.GET(employeeId), {
        headers: addAuthHeader()
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch employee data:', err);
      throw new Error('Failed to load employee data');
    }
  };

  // ================= Simple per-row Replace action =================
  const handleReplaceClick = (doc) => {
    // Store target and open hidden file input
    setReplaceTarget(doc);
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = '';
      replaceFileInputRef.current.click();
    }
  };

  const onReplaceFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !replaceTarget || !employee?.uid) return;

    try {
      setUploading(true);
      const backendDocType = normalizeBackendDocType(replaceTarget.documentType || replaceTarget.category || replaceTarget.type);
      console.log('Replace flow resolved backendDocType:', backendDocType, 'from', replaceTarget.documentType || replaceTarget.category || replaceTarget.type);
      const isPassportCategory = String(backendDocType).toLowerCase() === 'passport_copy';

      // Upload files to file service first
      const uploads = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'USER_DOCUMENT');
        formData.append('documentType', backendDocType);
        formData.append('employeeUid', employee.uid);
        formData.append('fileName', file.name);
        const res = await axios.post(API_ENDPOINTS.FILES.UPLOAD, formData, {
          headers: addAuthHeader({ 'Content-Type': 'multipart/form-data' })
        });
        const filePath = res?.data?.data?.filePath || res?.data?.filePath;
        const fileId = filePath || res?.data?.fileId;
        if (!fileId) throw new Error('Failed to resolve uploaded file identifier');
        uploads.push({ fileId, filePath: filePath || null, fileName: file.name, fileSize: file.size, file });
      }

      // Use consolidated passport update for passport documents
      if (isPassportCategory) {
        await handleConsolidatedPassportUpdate({
          backendDocType,
          uploads,
          expiryDate: replaceTarget.expiryDate || null,
          description: replaceTarget.description || ''
        });
        setSuccess('Passport replaced successfully');
      } else {
        // Save metadata with replaceCategory flag for non-passport documents
        await saveMultipleMetadata({
          backendDocType,
          uploads,
          expiryDate: replaceTarget.expiryDate || null,
          description: replaceTarget.description || '',
          replaceCategory: true
        });
        setSuccess('Document replaced successfully');
      }
    } catch (err) {
      console.error('Replace error:', err);
      setError('Failed to replace document. Please try again.');
    } finally {
      setUploading(false);
      setReplaceTarget(null);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
    }
  };

  // Fetch employee documents from API
  const fetchEmployeeDocuments = async (uid) => {
    try {
      console.log('Fetching documents for employee UID:', uid);
      const response = await axios.get(`${API_BASE_V1}/users/${uid}/documents`, {
        headers: addAuthHeader()
      });
      console.log('Documents response:', response.data);
      
      // Map the API response to our document format
      const raw = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const formattedDocs = raw.map(doc => ({
        id: doc.id,
        name: doc.fileName,
        type: doc.fileType || doc.contentType,
        category: doc.documentType,
        uploadDate: doc.createdDate || doc.createdAt,
        expiryDate: doc.expiryDate,
        status: doc.expiryDate ? 
          (new Date(doc.expiryDate) < new Date() ? 'Expired' : 
           calculateDaysUntilExpiry(doc.expiryDate) < 30 ? 'Expiring Soon' : 'Valid') : 'Valid',
        size: doc.fileSize,
        fileId: doc.fileId,
        filePath: doc.filePath,
        isCurrent: (doc.isCurrent !== undefined ? doc.isCurrent : (doc.active !== undefined ? doc.active : true)),
        active: doc.active
      }));
      
      setDocuments(formattedDocs);
      return formattedDocs;
    } catch (err) {
      console.error('Failed to fetch employee documents:', err);
      setError('Failed to load documents. Please try again later.');
      return [];
    }
  };

  // Fetch employee and documents data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get employee data
        const employeeData = await fetchEmployeeData(id);
        setEmployee(employeeData);
        
        // Get employee documents if we have a UID
        if (employeeData && employeeData.uid) {
          await fetchEmployeeDocuments(employeeData.uid);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load employee data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Handle file selection for upload
  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setUploadData({
      ...uploadData,
      files: selected
    });
    if (uploadErrors.files) {
      setUploadErrors({ ...uploadErrors, files: null });
    }
  };

  // Handle upload form field changes
  const handleUploadChange = (field) => (e) => {
    const value = e.target.value;
    setUploadData({
      ...uploadData,
      [field]: value
    });
    
    // Clear error for this field when changed
    if (uploadErrors[field]) {
      setUploadErrors({
        ...uploadErrors,
        [field]: null
      });
    }
  };

  // Open upload dialog
  const handleOpenUploadDialog = () => {
    setUploadData({
      files: [],
      category: '',
      expiryDate: '',
      description: ''
    });
    setUploadErrors({});
    setOpenUploadDialog(true);
  };

  // Close upload dialog
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (doc) => {
    setSelectedDocument(doc);
    setOpenDeleteDialog(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedDocument(null);
  };

  // Validate document upload form
  const validateUpload = () => {
    const errors = {};
    
    if (!uploadData.files || uploadData.files.length === 0) {
      errors.files = 'Please select at least one file to upload';
    } else {
      const tooBig = uploadData.files.find(f => f.size > 10 * 1024 * 1024);
      if (tooBig) errors.files = `File ${tooBig.name} exceeds 10MB limit`;
    }
    
    if (!uploadData.category) {
      errors.category = 'Document category is required';
    }
    
    return errors;
  };

  // Handle document upload
  const handleUpload = async () => {
    const validationErrors = validateUpload();
    
    if (Object.keys(validationErrors).length > 0) {
      setUploadErrors(validationErrors);
      return;
    }
    
    setUploading(true);
    
    try {
      console.log('Uploading document for employee:', employee);
      const backendDocType = normalizeBackendDocType(uploadData.category);
      console.log('Upload flow resolved backendDocType:', backendDocType, 'from', uploadData.category);
      const isPassportCategory = String(backendDocType).toLowerCase() === 'passport_copy';

      // Upload all files first to get identifiers
      const uploads = [];
      for (const file of uploadData.files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', 'USER_DOCUMENT');
        formData.append('documentType', backendDocType);
        formData.append('employeeUid', employee?.uid || id);
        formData.append('fileName', file.name);
        const res = await axios.post(API_ENDPOINTS.FILES.UPLOAD, formData, {
          headers: addAuthHeader({ 'Content-Type': 'multipart/form-data' })
        });
        const filePath = res?.data?.data?.filePath || res?.data?.filePath;
        const fileId = filePath || res?.data?.fileId;
        if (!fileId) throw new Error('Failed to resolve uploaded file identifier');
        uploads.push({ fileId, filePath: filePath || null, fileName: file.name, fileSize: file.size, file });
      }

      // For passport category, use consolidated passport update flow
      if (isPassportCategory) {
        await handleConsolidatedPassportUpdate({ backendDocType, uploads, expiryDate: uploadData.expiryDate || null, description: uploadData.description || '' });
        setSuccess('Passport uploaded and set as active');
        handleCloseUploadDialog();
        return;
      }

      // Check if category exists; prompt replace-all flow
      const exists = (documents || []).some(d => {
        const type = d.category || d.documentType || d.type;
        return String(type).toLowerCase() === String(backendDocType).toLowerCase();
      });

      if (exists) {
        setPendingUpload({ files: uploadData.files, backendDocType, uploads, expiryDate: uploadData.expiryDate || null, description: uploadData.description || '' });
        setReplaceDialogOpen(true);
        return;
      }

      // No existing docs in the category; save all as Active
      await saveMultipleMetadata({ backendDocType, uploads, expiryDate: uploadData.expiryDate || null, description: uploadData.description || '', replaceCategory: false });
      setSuccess('Documents uploaded successfully');
      handleCloseUploadDialog();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Save multiple files' metadata; backend should honor replaceCategory by inactivating existing and setting new as active
  const saveMultipleMetadata = async ({ backendDocType, uploads, expiryDate, description, replaceCategory }) => {
    if (replaceCategory && uploads.length > 1) {
      // Use bulk API for replace operations with multiple files
      await saveBulkDocuments({ backendDocType, uploads, expiryDate, description, replaceCategory });
    } else {
      // Use individual API for single file uploads or non-replace operations
      await saveIndividualDocuments({ backendDocType, uploads, expiryDate, description, replaceCategory });
    }
    
    await fetchEmployeeDocuments(employee.uid);
  };

  // Consolidated passport update function to prevent multiple documentGroupId creation
  const handleConsolidatedPassportUpdate = async ({ backendDocType, uploads, expiryDate, description }) => {
    console.log('Starting consolidated passport update for', uploads.length, 'files');
    
    // Step 1: Extract passport data first (before saving document metadata)
    let extractedData = {};
    try {
      const firstUpload = uploads[0];
      if (firstUpload && firstUpload.file) {
        const payload = new FormData();
        payload.append('file', firstUpload.file);
        payload.append('documentType', 'PASSPORT_COPY');

        const exRes = await axios.post(API_ENDPOINTS.DOCUMENT_PROCESSING.EXTRACT, payload, {
          headers: addAuthHeader({ 'Content-Type': 'multipart/form-data' })
        });

        const exData = exRes?.data?.data || exRes?.data || {};
        extractedData.passportNumber = exData.passportNumber || exData.number || null;
        extractedData.passportExpiry = exData.passportExpiryDate || exData.expiryDate || null;
        console.log('Extracted passport data:', extractedData);
      }
    } catch (exErr) {
      console.warn('Passport extraction failed, continuing with manual data:', exErr.message);
    }

    // Step 2: Save document metadata with extracted data (single documentGroupId creation)
    await savePassportWithExtractedData({ backendDocType, uploads, expiryDate, description, extractedData });

    // Step 3: Update employee profile with extracted data (no additional document save)
    try {
      const updatePayload = {};
      if (extractedData.passportNumber) updatePayload.passportNumber = extractedData.passportNumber;
      if (extractedData.passportExpiry) updatePayload.passportExpiry = extractedData.passportExpiry;
      
      if (Object.keys(updatePayload).length > 0) {
        await axios.put(API_ENDPOINTS.EMPLOYEES.UPDATE(id), updatePayload, {
          headers: addAuthHeader({ 'Content-Type': 'application/json' })
        });
        setEmployee(prev => ({ ...prev, ...updatePayload }));
        console.log('Updated employee profile with extracted data');
      }
    } catch (profileErr) {
      console.warn('Employee profile update failed:', profileErr.message);
    }
  };

  // Save passport documents with extracted metadata (creates only one documentGroupId)
  const savePassportWithExtractedData = async ({ backendDocType, uploads, expiryDate, description, extractedData }) => {
    // Generate a unique session ID to group all passport files under same documentGroupId
    const sessionId = `passport-update-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log('Using session ID for passport grouping:', sessionId);

    for (let i = 0; i < uploads.length; i++) {
      const u = uploads[i];
      const metadataForm = new FormData();
      metadataForm.append('file', u.file);
      metadataForm.append('documentType', backendDocType);
      metadataForm.append('fileId', u.fileId);
      metadataForm.append('fileName', u.fileName);
      metadataForm.append('fileSize', String(u.fileSize));
      
      // Include extracted passport data in the document metadata
      if (extractedData.passportNumber) {
        metadataForm.append('documentNumber', extractedData.passportNumber);
      }
      if (extractedData.passportExpiry) {
        metadataForm.append('expiryDate', extractedData.passportExpiry);
      }
      if (expiryDate) {
        metadataForm.append('expiryDate', expiryDate);
      }
      if (description) {
        metadataForm.append('description', description);
      }
      
      // Mark as replace operation for first file only
      if (i === 0) {
        metadataForm.append('replaceCategory', 'true');
      }

      const saveResponse = await axios.post(`${API_BASE_V1}/users/${employee.uid}/documents`, metadataForm, {
        headers: {
          ...addAuthHeader({ 'Content-Type': 'multipart/form-data' }),
          'X-Upload-Session-Id': sessionId  // Use session ID for grouping
        }
      });
      console.log('Passport document saved:', saveResponse.data);
    }
    
    await fetchEmployeeDocuments(employee.uid);
  };

  // Save multiple documents using bulk API (groups them under same documentGroupId)
  const saveBulkDocuments = async ({ backendDocType, uploads, expiryDate, description, replaceCategory }) => {
    // Generate a unique session ID to group all files under same documentGroupId
    const sessionId = `bulk-upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log('Using session ID for bulk upload grouping:', sessionId);
    
    // First upload all files individually to get file paths, using session ID for grouping
    const uploadedFiles = [];
    
    for (let i = 0; i < uploads.length; i++) {
      const u = uploads[i];
      const metadataForm = new FormData();
      metadataForm.append('file', u.file);
      metadataForm.append('documentType', backendDocType);
      metadataForm.append('fileId', u.fileId);
      metadataForm.append('fileName', u.fileName);
      metadataForm.append('fileSize', String(u.fileSize));
      
      // Mark as replace operation for first file only
      if (i === 0 && replaceCategory) {
        metadataForm.append('replaceCategory', 'true');
      }
      
      // Add flag to skip employee document operations during bulk upload
      metadataForm.append('skipEmployeeDocuments', 'true');
      
      const uploadResponse = await axios.post(`${API_BASE_V1}/users/${employee.uid}/documents`, metadataForm, {
        headers: {
          ...addAuthHeader({ 'Content-Type': 'multipart/form-data' }),
          'X-Upload-Session-Id': sessionId  // Use session ID for grouping
        }
      });
      
      uploadedFiles.push({
        filePath: uploadResponse.data.filePath,
        fileName: u.fileName,
        contentType: u.file.type,
        fileSize: u.fileSize
      });
    }

    // Skip bulk API call - employee document groups will be created on "Update Profile"
    // This prevents creating intermediate document groups during bulk upload
    console.log('Skipping bulk employee document creation - will be handled on Update Profile');
  };

  // Save documents individually (for single uploads or non-replace operations)
  const saveIndividualDocuments = async ({ backendDocType, uploads, expiryDate, description, replaceCategory }) => {
    // Generate a unique session ID to group multiple files under same documentGroupId
    const sessionId = uploads.length > 1 ? `individual-upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` : null;
    if (sessionId) {
      console.log('Using session ID for individual upload grouping:', sessionId);
    }
    
    let first = true;
    for (const u of uploads) {
      const metadataForm = new FormData();
      metadataForm.append('file', u.file);
      metadataForm.append('documentType', backendDocType);
      metadataForm.append('fileId', u.fileId);
      metadataForm.append('fileName', u.fileName);
      metadataForm.append('fileSize', String(u.fileSize));
      if (expiryDate) metadataForm.append('expiryDate', expiryDate);
      if (description) metadataForm.append('description', description);
      
      if (replaceCategory && first) {
        metadataForm.append('replaceCategory', 'true');
      }

      const headers = {
        ...addAuthHeader({ 'Content-Type': 'multipart/form-data' })
      };
      
      // Add session ID header for multiple file uploads
      if (sessionId) {
        headers['X-Upload-Session-Id'] = sessionId;
      }
      
      // Add flag to skip employee document operations during bulk upload
      metadataForm.append('skipEmployeeDocuments', 'true');

      const saveResponse = await axios.post(`${API_BASE_V1}/users/${employee.uid}/documents`, metadataForm, {
        headers
      });
      console.log('Document metadata save response:', saveResponse.data);
      first = false;
    }
  };

  // Handle document deletion
  const handleDeleteDocument = async () => {
    if (!selectedDocument) return;
    
    try {
      console.log('Deleting document:', selectedDocument);
      
      // Call the API to delete the document
      await axios.delete(`${API_BASE_V1}/users/${employee.uid}/documents/${selectedDocument.id}`, {
        headers: addAuthHeader()
      });
      
      // Refresh the document list from the API
      await fetchEmployeeDocuments(employee.uid);
      
      setSuccess('Document deleted successfully');
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document. Please try again.');
    }
  };

  // Download document using the UserDocumentController endpoint
  const handleDocumentDownload = async (doc) => {
    console.log('DOWNLOAD - Document clicked:', doc);
    console.log('DOWNLOAD - Current employee:', employee);
    
    if (!doc) {
      console.error('DOWNLOAD - Document object is null or undefined');
      setError('Document information is incomplete');
      return;
    }
    
    if (!employee || !employee.uid) {
      console.error('DOWNLOAD - Employee UID is missing');
      setError('Employee information is missing');
      return;
    }
    
    try {
      const token = getAuthToken();
      const downloadUrl = `${API_BASE_V1}/users/${employee.uid}/documents/${doc.id}/download`;
      const res = await fetch(downloadUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Download failed: ${res.status} ${res.statusText}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.fileName || `${doc.id}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      console.error('Error downloading document', e);
      setError('Failed to download document');
    }
  };

// Preview document by opening it in a new tab
const handleDocumentPreview = async (doc) => {
  console.log('PREVIEW - Document clicked:', doc);
  console.log('PREVIEW - Current employee:', employee);
  
  if (!doc) {
    console.error('PREVIEW - Document object is null or undefined');
    setError('Document information is incomplete');
    return;
  }
  
  if (!employee || !employee.uid) {
    console.error('PREVIEW - Employee UID is missing');
    setError('Employee information is missing');
    return;
  }
  
  try {
    const token = getAuthToken();
    // Use the standard download endpoint; we'll open as a blob for preview
    const previewUrl = `${API_BASE_V1}/users/${employee.uid}/documents/${doc.id}/download`;
    const res = await fetch(previewUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`Preview failed: ${res.status} ${res.statusText}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    // Open in the same window
    window.location.href = blobUrl;
  } catch (e) {
    console.error('Error previewing document', e);
    setError('Failed to preview document');
  }
};

  // Navigate back to employee detail
  const handleBack = () => {
    navigate(ROUTES.CUSTOMER.EMPLOYEES.DETAIL(id));
  };

  // Clear success message
  const handleCloseSuccess = () => {
    setSuccess(null);
  };

  // Clear error message
  const handleCloseError = () => {
    setError(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 5, mb: 5, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading documents...
        </Typography>
      </Container>
    );
  }

  if (!employee) {
    return (
      <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
        <Alert severity="error">
          Employee not found or you don't have permission to access their documents.
        </Alert>
        <Box mt={3} textAlign="center">
          <Button 
            variant="contained" 
            onClick={() => navigate(ROUTES.CUSTOMER.EMPLOYEES.LIST)}
            startIcon={<ArrowBack />}
          >
            Back to Employee List
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h5" component="h1">
              Document Management
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {employee.firstName} {employee.lastName} • {employee.employeeId}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={handleOpenUploadDialog}
          >
            Upload Document
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 4 }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Employee Documents
          </Typography>
          <Button 
            startIcon={<Refresh />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Box>

        <Divider />

        {documents.length === 0 ? (
          <Box p={4} textAlign="center">
            <Description sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Documents Found
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              This employee doesn't have any documents uploaded yet.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={handleOpenUploadDialog}
            >
              Upload Document
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Document Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>File Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Document Type</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Uploaded</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Expiry Date</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Size</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Status</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Actions</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const grouped = documents.reduce((acc, d) => {
                    const cat = d.documentType || d.type || d.category || 'Uncategorized';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(d);
                    return acc;
                  }, {});

                  return Object.entries(grouped).map(([category, docs]) => (
                    <React.Fragment key={category}>
                      <TableRow selected>
                        <TableCell colSpan={8} sx={{ backgroundColor: 'action.hover' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{category}</Typography>
                        </TableCell>
                      </TableRow>
                      {docs.map((doc) => {
                        const expiryDate = doc.expiryDate ? new Date(doc.expiryDate) : null;
                        const now = new Date();
                        const isExpired = expiryDate ? expiryDate < now : false;
                        const daysToExpiry = expiryDate ? (expiryDate - now) / (1000 * 60 * 60 * 24) : null;
                        const isExpiringSoon = expiryDate ? daysToExpiry <= 30 && !isExpired : false;

                        return (
                          <TableRow key={doc.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Description sx={{ mr: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{doc.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{doc.fileName || doc.name || 'N/A'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{doc.documentType || doc.type || doc.category || 'N/A'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                <strong>Uploaded:</strong> {doc.uploadDate ? formatDate(doc.uploadDate, true) : formatDate(doc.createdAt, true) || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {doc.expiryDate ? (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography 
                                    variant="subtitle2" 
                                    sx={{ 
                                      fontSize: '0.875rem', 
                                      fontWeight: 500,
                                      color: isExpired ? 'error.main' : isExpiringSoon ? 'warning.main' : 'inherit' 
                                    }}
                                  >
                                    {formatDate(doc.expiryDate)}
                                  </Typography>
                                  {isExpiringSoon && !isExpired && (
                                    <Warning fontSize="small" sx={{ ml: 0.5, color: 'warning.main' }} />
                                  )}
                                </Box>
                              ) : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                {formatFileSize(doc.size)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Chip 
                                  label={(doc.isCurrent === false || doc.active === false) ? 'Inactive' : 'Active'}
                                  size="small"
                                  color={(doc.isCurrent === false || doc.active === false) ? 'default' : 'success'}
                                  sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                                />
                                <Chip 
                                  label={doc.expiryDate ? (isExpired ? 'Expired' : isExpiringSoon ? 'Expiring Soon' : 'Valid') : 'No Expiry'}
                                  size="small"
                                  color={doc.expiryDate ? (isExpired ? 'error' : isExpiringSoon ? 'warning' : 'success') : 'default'}
                                  sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton 
                                size="small" 
                                title="Preview"
                                onClick={() => handleDocumentPreview(doc)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                title="Replace"
                                onClick={() => handleReplaceClick(doc)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                title="Download"
                                onClick={() => handleDocumentDownload(doc)}
                              >
                                <CloudDownload fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                title="Delete"
                                color="error"
                                onClick={() => handleOpenDeleteDialog(doc)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ));
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Document Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              id="document-upload"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              multiple
            />
            <label htmlFor="document-upload">
              <Paper
                component="div"
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  mb: 3,
                  borderColor: uploadErrors.file ? 'error.main' : '#ccc',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(0, 0, 0, 0.01)'
                  }
                }}
              >
                <CloudUpload color="primary" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {uploadData.files && uploadData.files.length > 0 ? 'Change Files' : 'Click to select files'}
                </Typography>
                {uploadData.files && uploadData.files.length > 0 ? (
                  <Box>
                    {uploadData.files.map((f, idx) => (
                      <Typography key={idx} variant="body2">
                        • {f.name} ({formatFileSize(f.size)})
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Supported formats: PDF, JPG, PNG (max size: 10MB). You can select multiple files.
                  </Typography>
                )}
                {uploadErrors.files && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {uploadErrors.files}
                  </Typography>
                )}
              </Paper>
            </label>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!uploadErrors.category} required>
                  <InputLabel id="category-label">Document Category</InputLabel>
                  <Select
                    labelId="category-label"
                    label="Document Category"
                    value={uploadData.category}
                    onChange={handleUploadChange('category')}
                  >
                    {documentCategories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>{category.label}</MenuItem>
                    ))}
                  </Select>
                  {uploadErrors.category && (
                    <FormHelperText error>{uploadErrors.category}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Expiry Date (if applicable)"
                  type="date"
                  value={uploadData.expiryDate}
                  onChange={handleUploadChange('expiryDate')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="Leave blank if document does not expire"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (optional)"
                  multiline
                  rows={2}
                  value={uploadData.description}
                  onChange={handleUploadChange('description')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseUploadDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleUpload}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace Confirmation Dialog */}
      <Dialog open={replaceDialogOpen} onClose={() => setReplaceDialogOpen(false)}>
        <DialogTitle>Replace Existing Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This category already has existing files. Replacing will mark all existing files under this category as Inactive and add your {pendingUpload?.uploads?.length || uploadData.files.length} new file(s) as Active.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => { setReplaceDialogOpen(false); setPendingUpload(null); }}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={async () => {
              try {
                if (!pendingUpload) return;
                
                const isPassportCategory = String(pendingUpload.backendDocType).toLowerCase() === 'passport_copy';
                
                if (isPassportCategory) {
                  // Use consolidated passport update for passport documents
                  await handleConsolidatedPassportUpdate({
                    backendDocType: pendingUpload.backendDocType,
                    uploads: pendingUpload.uploads,
                    expiryDate: pendingUpload.expiryDate,
                    description: pendingUpload.description
                  });
                  setSuccess('Passport category replaced successfully');
                } else {
                  // Use regular replace flow for non-passport documents
                  await saveMultipleMetadata({
                    backendDocType: pendingUpload.backendDocType,
                    uploads: pendingUpload.uploads,
                    expiryDate: pendingUpload.expiryDate,
                    description: pendingUpload.description,
                    replaceCategory: true
                  });
                  setSuccess('Category replaced successfully');
                }
                
                setReplaceDialogOpen(false);
                setPendingUpload(null);
                handleCloseUploadDialog();
              } catch (e) {
                console.error('Replace failed:', e);
                setError('Failed to replace document.');
              }
            }}
          >
            Replace
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this document: <strong>{selectedDocument?.name}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDeleteDialog}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteDocument}
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden input for per-row Replace action */}
      <input
        type="file"
        ref={replaceFileInputRef}
        style={{ display: 'none' }}
        onChange={onReplaceFileChange}
      />
    </Container>
  );
}

export default DocumentManagement;
